import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, Eye, EyeOff, X, Phone } from 'lucide-react';
import OTPLogin from './OTPLogin';
import { navigate } from '../utils/navigation';

interface LoginProps {
  onClose: () => void;
  onSwitchToSignup: () => void;
  redirectAfterLogin?: string;
}

const Login: React.FC<LoginProps> = ({ onClose, onSwitchToSignup, redirectAfterLogin }) => {
  const [formData, setFormData] = useState({
    phone: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showOTPLogin, setShowOTPLogin] = useState(false);
  const { login, loading, error, clearError } = useAuth();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (error) {
      clearError();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.phone.trim() || !formData.password.trim()) {
      return;
    }

    try {
      console.log('Login attempt for phone:', formData.phone);
      await login(formData.phone, formData.password);
      console.log('Login successful, redirectAfterLogin:', redirectAfterLogin);

      // Import necessary functions for navigation
      const { goToAdmin, goToCommunity, goToCommunityAdmin, goToVendorAdmin } = await import('../utils/navigation');
      const { useAuth } = await import('../contexts/AuthContext');
      
      // Get current auth context
      const { isAdmin, isCommittee, isVendor } = useAuth();
      
      // Determine redirect target based on user role
      let target = redirectAfterLogin;
      if (!target) {
        if (isAdmin) {
          target = '/admin';
          goToAdmin();
        } else if (isCommittee) {
          target = '/community';
          goToCommunity();
        } else if (isVendor) {
          target = '/community';
          goToCommunity();
        } else {
          target = '/';
          navigate(target);
        }
      } else {
        navigate(target);
      }
      
      onClose();
    } catch (err) {
      console.error('Login error:', err);
      // Error is handled by the auth context
    }
  };

  if (showOTPLogin) {
    return (
      <OTPLogin
        onClose={onClose}
        onBack={() => setShowOTPLogin(false)}
        redirectAfterLogin={redirectAfterLogin}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-4 sm:p-6 md:p-8 relative max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-6 w-6" />
        </button>

        <div className="text-center mb-6 sm:mb-8">
          <div className="flex justify-center mb-4">
            <img 
              src="/logo.png" 
              alt="Durga Puja Network" 
              className="h-20 w-20 sm:h-24 sm:w-24 object-contain"
              onError={(e) => e.currentTarget.style.display = 'none'}
            />
          </div>
          <h2 className="font-runtime text-2xl sm:text-3xl font-bold text-black mb-2">
            Welcome Back
          </h2>
          <p className="font-lucy-rose text-gray-600">
            Sign in to your Durga Puja Network account
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2 font-lucy-rose">
              Phone Number or Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose"
                placeholder="Enter your phone number or email"
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2 font-lucy-rose">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose"
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-600 text-sm font-lucy-rose">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-dpn-pink hover:bg-dpn-raspberry text-white font-lucy-rose font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
            ) : null}
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 space-y-4">
          {/* OTP Login Option */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => setShowOTPLogin(true)}
              className="w-full flex items-center justify-center space-x-2 py-3 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-lucy-rose font-medium text-gray-700"
            >
              <Phone className="h-5 w-5" />
              <span>Login with OTP instead</span>
            </button>
          </div>

          {/* Forgot Password */}

          {/* Sign up link */}
          <div className="text-center border-t pt-4">
            <p className="font-lucy-rose text-sm text-gray-600">
              Don't have an account?{' '}
              <button
                onClick={onSwitchToSignup}
                className="text-dpn-pink hover:text-dpn-raspberry font-medium transition-colors"
              >
                Sign up
              </button>
            </p>
          </div>
          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate('/forgot-password');
              }}
              className="text-dpn-pink hover:text-dpn-raspberry font-medium transition-colors font-lucy-rose text-sm"
            >
              Forgot your password?
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 